import BaseDropdownController from './base_dropdown_controller'

export default class extends BaseDropdownController {
  static targets = ["applyBtn", "dropdownToggle", "countDisplay", "clearBtn", "scheduledSwitch"]

  connect() {
    super.connect()
    this.initializeFromFormState()
    this.updateApplyButton()
    this.updateCountDisplay()

    this.boundHandleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)
    document.addEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)
  }

  disconnect() {
    super.disconnect()
    document.removeEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)
  }

  handleTurboFrameLoad(event) {
    if (event.target.id === 'auction_results') {
      setTimeout(() => {
        this.updateCountDisplay()
      }, 50)
    }
  }

  initializeFromFormState() {
    const form = document.getElementById('auction-filters-form')
    if (!form) return

    const floorCheckboxes = form.querySelectorAll('[data-floor-checkbox]')
    floorCheckboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const floor = checkbox.getAttribute('data-floor-checkbox')
        const btn = this.element.querySelector(`.floor-btn[data-value="${floor}"]`)
        if (btn) btn.classList.add('active')
      }
    })

    const roundCheckboxes = form.querySelectorAll('[data-round-checkbox]')
    roundCheckboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const round = checkbox.getAttribute('data-round-checkbox')
        const btn = this.element.querySelector(`.round-btn[data-value="${round}"]`)
        if (btn) btn.classList.add('active')
      }
    })

    const startDateField = form.querySelector('[name="start_date"]')
    const endDateField = form.querySelector('[name="end_date"]')
    if (startDateField?.value) {
      const auctionDateFromInput = this.element.querySelector('[data-target="auctionDateFrom"]')
      if (auctionDateFromInput) auctionDateFromInput.value = startDateField.value
    }
    if (endDateField?.value) {
      const auctionDateToInput = this.element.querySelector('[data-target="auctionDateTo"]')
      if (auctionDateToInput) auctionDateToInput.value = endDateField.value
    }

    const onlyScheduledField = form.querySelector('[name="only_scheduled"]')
    if (this.hasScheduledSwitchTarget && onlyScheduledField) {
      this.scheduledSwitchTarget.checked = onlyScheduledField.value === 'true'
    }
  }

  toggleFloor(event) {
    event.currentTarget.classList.toggle('active')
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  toggleRound(event) {
    event.currentTarget.classList.toggle('active')
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  onInputChange() {
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  onInputBlur() {
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  toggleOnlyScheduled() {
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  applyFilters() {
    this.syncWithFormFields()
    this.closeDropdown()
    this.closeOffcanvas()
    this.submitSharedForm()
  }

  syncWithFormFields() {
    const form = document.getElementById('auction-filters-form')
    if (!form) return

    const selectedFloors = Array.from(this.element.querySelectorAll('.floor-btn.active')).map(btn => btn.dataset.value)
    const floorCheckboxes = form.querySelectorAll('[data-floor-checkbox]')
    floorCheckboxes.forEach(checkbox => {
      const floor = checkbox.getAttribute('data-floor-checkbox')
      checkbox.checked = selectedFloors.includes(floor)
    })

    const selectedRounds = Array.from(this.element.querySelectorAll('.round-btn.active')).map(btn => btn.dataset.value)
    const roundCheckboxes = form.querySelectorAll('[data-round-checkbox]')
    roundCheckboxes.forEach(checkbox => {
      const round = checkbox.getAttribute('data-round-checkbox')
      checkbox.checked = selectedRounds.includes(round)
    })

    const startDateField = form.querySelector('[name="start_date"]')
    const endDateField = form.querySelector('[name="end_date"]')
    const auctionDateFromInput = this.element.querySelector('[data-target="auctionDateFrom"]')
    const auctionDateToInput = this.element.querySelector('[data-target="auctionDateTo"]')
    if (startDateField) startDateField.value = auctionDateFromInput?.value || ''
    if (endDateField) endDateField.value = auctionDateToInput?.value || ''

    const onlyScheduledField = form.querySelector('[name="only_scheduled"]')
    if (onlyScheduledField && this.hasScheduledSwitchTarget) {
      onlyScheduledField.value = this.scheduledSwitchTarget.checked.toString()
    }
  }

  clearAllFilters() {
    this.element.querySelectorAll('.floor-btn.active').forEach(btn => {
      btn.classList.remove('active')
    })

    this.element.querySelectorAll('.round-btn.active').forEach(btn => {
      btn.classList.remove('active')
    })

    const auctionDateFromInput = this.element.querySelector('[data-target="auctionDateFrom"]')
    const auctionDateToInput = this.element.querySelector('[data-target="auctionDateTo"]')
    if (auctionDateFromInput) auctionDateFromInput.value = ''
    if (auctionDateToInput) auctionDateToInput.value = ''
    if (this.hasScheduledSwitchTarget) this.scheduledSwitchTarget.checked = false

    this.syncWithFormFields()
    this.submitSharedForm()

    this.updateApplyButton()
    this.updateCountDisplay()
  }

  closeDropdown() {
    const toggleButton = this.dropdownToggleTarget
    if (toggleButton) {
      const dropdown = bootstrap.Dropdown.getInstance(toggleButton)
      if (dropdown) {
        dropdown.hide()
      }
      toggleButton.blur()
    }
  }

  updateApplyButton() {
    if (!this.hasApplyBtnTarget) return
    this.applyBtnTarget.disabled = false
  }

  updateCountDisplay() {
    if (!this.hasCountDisplayTarget) return

    const activeCount = this.countAppliedFilters()
    const baseText = this.countDisplayTarget.textContent.replace(/\s*\(\d+\)/, '')

    if (activeCount === 0) {
      this.countDisplayTarget.textContent = baseText.trim()
    } else {
      this.countDisplayTarget.textContent = `${baseText.trim()} (${activeCount})`
    }

    if (this.hasDropdownToggleTarget) {
      if (activeCount === 0) {
        this.dropdownToggleTarget.classList.remove('filter-dropdown-active')
      } else {
        this.dropdownToggleTarget.classList.add('filter-dropdown-active')
      }
    }
  }

  countAppliedFilters() {
    let count = 0

    const floorButtons = this.element.querySelectorAll('.floor-btn.active')
    count += floorButtons.length

    const roundButtons = this.element.querySelectorAll('.round-btn.active')
    count += roundButtons.length

    const auctionDateFromInput = this.element.querySelector('[data-target="auctionDateFrom"]')
    const auctionDateToInput = this.element.querySelector('[data-target="auctionDateTo"]')
    if (auctionDateFromInput?.value && auctionDateFromInput.value.trim()) count++
    if (auctionDateToInput?.value && auctionDateToInput.value.trim()) count++

    if (this.hasScheduledSwitchTarget && this.scheduledSwitchTarget.checked) count++

    return count
  }
}
